import { useCallback, useEffect, useMemo, useState } from 'react';
import { uuid } from '../utils/format';
import { itemLineTotal, modifiersSignature } from '../utils/restaurantModifiers';

const DEFAULT_TABLES = [
  ...Array.from({ length: 8 }, (_, index) => ({ id: `salon-${index + 1}`, nombre: `Mesa ${index + 1}`, zona: 'Salón', capacidad: index < 4 ? 4 : 2 })),
  ...Array.from({ length: 4 }, (_, index) => ({ id: `terraza-${index + 9}`, nombre: `Mesa ${index + 9}`, zona: 'Terraza', capacidad: 4 })),
];

function storageKey(tenantId) {
  return `integranube_restaurant:v1:${tenantId || 'default'}`;
}

function emptyState() {
  return { version: 1, tables: DEFAULT_TABLES, orders: [], takeawayCounter: 1 };
}

function loadState(tenantId) {
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    return {
      ...emptyState(),
      ...parsed,
      tables: Array.isArray(parsed?.tables) && parsed.tables.length ? parsed.tables : DEFAULT_TABLES,
      orders: Array.isArray(parsed?.orders) ? parsed.orders : [],
    };
  } catch (_) {
    return emptyState();
  }
}

function orderTotal(order) {
  return (order?.items || []).reduce((sum, item) => sum + itemLineTotal(item), 0);
}


function deriveOrderKitchenStatus(items = []) {
  const statuses = new Set(items.map((item) => item.kitchenStatus).filter((status) => status && status !== 'draft'));
  if (statuses.has('sent')) return 'sent';
  if (statuses.has('preparing')) return 'preparing';
  if (statuses.has('ready')) return 'ready';
  if (statuses.has('served')) return 'served';
  return 'draft';
}

function customizationFrom(value = {}) {
  return {
    modifierSelections: Array.isArray(value?.modifierSelections) ? value.modifierSelections : [],
    note: String(value?.note || ''),
    criticalNote: Boolean(value?.criticalNote),
  };
}

export default function useRestaurantStore(tenantId) {
  const [state, setState] = useState(() => loadState(tenantId));

  useEffect(() => {
    setState(loadState(tenantId));
  }, [tenantId]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(tenantId), JSON.stringify(state));
    } catch (_) {}
  }, [state, tenantId]);

  const activeOrders = useMemo(() => state.orders.filter((order) => !['paid', 'cancelled'].includes(order.status)), [state.orders]);

  const openTable = useCallback((table) => {
    const existing = state.orders.find((order) => order.tableId === table.id && !['paid', 'cancelled'].includes(order.status));
    if (existing) return existing.id;

    const id = uuid();
    const now = new Date().toISOString();
    const order = {
      id,
      type: 'table',
      tableId: table.id,
      label: table.nombre,
      zona: table.zona,
      status: 'draft',
      createdAt: now,
      updatedAt: now,
      sentAt: null,
      persons: 1,
      note: '',
      items: [],
    };
    setState((current) => ({ ...current, orders: [...current.orders, order] }));
    return id;
  }, [state.orders]);

  const openTakeaway = useCallback(() => {
    const id = uuid();
    const now = new Date().toISOString();
    setState((current) => {
      const n = Number(current.takeawayCounter || 1);
      const order = {
        id,
        type: 'takeaway',
        tableId: null,
        label: `Para llevar #${n}`,
        zona: 'Para llevar',
        status: 'draft',
        createdAt: now,
        updatedAt: now,
        sentAt: null,
        persons: 1,
        note: '',
        items: [],
      };
      return { ...current, takeawayCounter: n + 1, orders: [...current.orders, order] };
    });
    return id;
  }, []);

  const updateOrder = useCallback((orderId, updater) => {
    setState((current) => ({
      ...current,
      orders: current.orders.map((order) => {
        if (order.id !== orderId) return order;
        const next = typeof updater === 'function' ? updater(order) : { ...order, ...updater };
        return { ...next, updatedAt: new Date().toISOString() };
      }),
    }));
  }, []);

  const setPersons = useCallback((orderId, persons) => {
    updateOrder(orderId, { persons: Math.max(1, Math.round(Number(persons || 1))) });
  }, [updateOrder]);

  const setOrderNote = useCallback((orderId, note) => {
    updateOrder(orderId, { note: String(note || '') });
  }, [updateOrder]);

  const addProduct = useCallback((orderId, producto, customization = {}) => {
    const normalized = customizationFrom(customization);
    const signature = modifiersSignature(normalized.modifierSelections, normalized.note, normalized.criticalNote);

    updateOrder(orderId, (order) => {
      const existingIndex = order.items.findIndex((item) => (
        Number(item.producto?.id) === Number(producto.id)
        && modifiersSignature(item.modifierSelections, item.note, item.criticalNote) === signature
        && item.kitchenStatus === 'draft'
      ));
      if (existingIndex >= 0) {
        const items = order.items.map((item, index) => index === existingIndex
          ? { ...item, cantidad: Number(item.cantidad || 0) + 1 }
          : item);
        return { ...order, items };
      }
      return {
        ...order,
        items: [...order.items, {
          id: uuid(),
          producto,
          cantidad: 1,
          note: normalized.note,
          criticalNote: normalized.criticalNote,
          modifierSelections: normalized.modifierSelections,
          kitchenStatus: 'draft',
        }],
      };
    });
  }, [updateOrder]);

  const updateItemCustomization = useCallback((orderId, itemId, customization = {}) => {
    const normalized = customizationFrom(customization);
    updateOrder(orderId, (order) => ({
      ...order,
      items: order.items.map((item) => item.id === itemId ? {
        ...item,
        ...normalized,
        kitchenStatus: item.kitchenStatus === 'served' ? 'served' : 'draft',
      } : item),
    }));
  }, [updateOrder]);

  const changeQuantity = useCallback((orderId, itemId, delta) => {
    updateOrder(orderId, (order) => ({
      ...order,
      items: order.items
        .map((item) => item.id === itemId ? {
          ...item,
          cantidad: Math.max(0, Number(item.cantidad || 0) + delta),
          kitchenStatus: item.kitchenStatus === 'served' ? 'served' : 'draft',
        } : item)
        .filter((item) => Number(item.cantidad || 0) > 0),
    }));
  }, [updateOrder]);

  const removeItem = useCallback((orderId, itemId) => {
    updateOrder(orderId, (order) => ({ ...order, items: order.items.filter((item) => item.id !== itemId) }));
  }, [updateOrder]);

  const setItemNote = useCallback((orderId, itemId, note) => {
    updateOrder(orderId, (order) => ({
      ...order,
      items: order.items.map((item) => item.id === itemId ? {
        ...item,
        note: String(note || ''),
        kitchenStatus: item.kitchenStatus === 'served' ? 'served' : 'draft',
      } : item),
    }));
  }, [updateOrder]);

  const sendKitchen = useCallback((orderId) => {
    const now = new Date().toISOString();
    updateOrder(orderId, (order) => ({
      ...order,
      status: 'sent',
      sentAt: now,
      items: order.items.map((item) => ({ ...item, kitchenStatus: item.kitchenStatus === 'draft' ? 'sent' : item.kitchenStatus })),
    }));
  }, [updateOrder]);

  const setKitchenStatus = useCallback((orderId, status, fromStatus = null) => {
    updateOrder(orderId, (order) => {
      const items = order.items.map((item) => {
        if (item.kitchenStatus === 'draft') return item;
        if (fromStatus && item.kitchenStatus !== fromStatus) return item;
        return { ...item, kitchenStatus: status };
      });
      return { ...order, status: deriveOrderKitchenStatus(items), items };
    });
  }, [updateOrder]);

  const markPaid = useCallback((orderId, saleResult = null) => {
    updateOrder(orderId, (order) => ({ ...order, status: 'paid', paidAt: new Date().toISOString(), saleResult }));
  }, [updateOrder]);

  const cancelOrder = useCallback((orderId) => {
    updateOrder(orderId, { status: 'cancelled', cancelledAt: new Date().toISOString() });
  }, [updateOrder]);

  return {
    tables: state.tables,
    orders: state.orders,
    activeOrders,
    orderTotal,
    openTable,
    openTakeaway,
    updateOrder,
    setPersons,
    setOrderNote,
    addProduct,
    updateItemCustomization,
    changeQuantity,
    removeItem,
    setItemNote,
    sendKitchen,
    setKitchenStatus,
    markPaid,
    cancelOrder,
  };
}
